/**
 * measureColumnWidths — read every column's **max-content** width off a live
 * `<table>`, so a column that is pinned by default can be pinned to what it
 * actually holds instead of to a constant.
 *
 * `data-col-key` on a `<th>` is the opt-in, and today exactly one family
 * carries it: the dynamic annex (AI-Review) columns. The other six static
 * columns are fluid — `widthFor` answers `undefined`, no width style is
 * emitted, and the browser already sizes them from content — so there is
 * nothing to measure. `contact_info` is pinned but deliberately NOT opted in:
 * "wider by default" is what cl.4 item 8 asked for, and content-fitting a
 * column is the opposite of honouring that.
 *
 * ## Why max-content, and why the table has to be un-stretched to read it
 *
 * `getBoundingClientRect()` on a `<th>` reports the width the column ENDED UP
 * with, which under `table-layout: auto` is a function of the table's own
 * width, not of the cell's content. Measured in Chromium on a 4-column table in
 * a 1400px box, the same column read:
 *
 * | table state                     | reported |
 * |---------------------------------|----------|
 * | `min-width: 100%` (the app's)   |     479  |
 * | shrink-to-fit                   |     234  |
 * | `width: max-content`            |     234  |
 *
 * So the pass sets `width: max-content` (plus `min-width: 0` to defeat the
 * table's own `min-w-full` floor, which is back as of the fill-column change) and clears the
 * per-`<th>` pins, because a pinned column reports its pin rather than its
 * content. Everything touched is restored in a `finally`.
 *
 * ## It is NET write-free, which is weaker than write-free
 *
 * An earlier version of this docstring claimed the pass "never writes a width",
 * and used that to argue it cannot argue with the imperative drag writer in
 * `ContactsTable` (the "TWO WRITERS, ONE `<th>`" note there). That was wrong in
 * a way worth spelling out, because the weaker property is still enough — but
 * only with the caller's help. The pass DOES write: it clears the pins, reads,
 * and puts them back. Between those two moments the `<th>` carries a width
 * neither React nor the drag put there.
 *
 * Net-neutral on the DOM is not neutral on the OUTCOME. The measurement is
 * taken with the drag's own pin cleared, so it can change `naturalWidths`,
 * which changes what the caller renders for that `<th>` — and `endResize`'s
 * no-commit branch then writes the PRE-refresh width while React's last render
 * holds the POST-refresh one, which React's prev===next diff will never
 * correct. That is the stranded-imperative-pin defect this file's neighbours
 * spent several review rounds closing.
 *
 * The caller is therefore required to skip the pass while a gesture is live.
 * `ContactsTable` does that with `resizeOrigin.current`. This module cannot
 * enforce it — it has no idea a drag exists — so it is stated here as a
 * contract rather than left implicit.
 *
 * ## The scroll offsets are part of the restore contract
 *
 * Under `width: max-content` nothing wraps, so every row becomes one line and
 * the table's HEIGHT collapses with it. Measured in Chromium on a 60-row table
 * whose cells wrap: 9541px tall becomes 1902px. The browser clamps the scroll
 * container's offset against that smaller extent immediately, and restoring the
 * WIDTH does not restore the offset — so a recruiter reading row 57 was
 * silently thrown back to row 9 (`scrollTop` 9143 -> 1504) every time the pass
 * ran. That is why `scroller.scrollTop` / `scrollLeft` are saved and restored
 * alongside the styles.
 *
 * The bug hides from a lazy test: an offset that still fits inside the
 * COLLAPSED extent is not clamped, so a probe that scrolls only part-way down
 * reports "preserved" and proves nothing. Scroll to the bottom.
 *
 * ## What the caller does and does NOT re-measure on
 *
 * **Window resize: no** — correct, and it should stay that way. `max-content`
 * is viewport-independent; un-stretching the table is the whole point of the
 * pass, so a narrower window cannot change what it answers.
 *
 * **Web-font load: yes, once per load** — since 2026-09-19 the web app serves
 * Inter (`@nu-assist/ui/fonts.css`, `font-display: swap`), so on a cold cache
 * text is laid out in the local fallback first and re-laid out when the face
 * arrives. Inter is wider than the system fonts it replaces, so a pass taken
 * before the swap reads the WRONG metrics and, being latched per signature, is
 * never redone: the columns stay sized for a font that is no longer on screen.
 * This paragraph used to say the hazard was "inert here only by accident",
 * because no `@font-face` existed; that accident is over.
 *
 * `pendingFontLoad` below is the fix, and the caller asks it AFTER the pass,
 * not before: a face is requested when text that needs it is first laid out,
 * and the pass's own `getBoundingClientRect()` is often that first layout (it
 * runs in a layout effect, ahead of paint). Asked before the pass, the set
 * still reports `loaded` for a face the pass is about to request. When a load
 * is in flight the caller waits for it and bumps its re-measure token ONCE;
 * the second pass sees `loaded` and the chain ends. A pass with no load in
 * flight schedules nothing, so paging and polling still cost no measurement.
 * That ending leans on the engine keeping `status` and `ready` in step, so the
 * caller also counts: `MAX_FONT_REMEASURES` below bounds the chain per mount.
 *
 * The re-measure goes through this same function, so the restore contract
 * above (styles, then every scroll offset) holds for it unchanged, and it only
 * moves `naturalWidths`: a width the user dragged is an override in
 * `useColumnWidths` and outranks any measurement.
 */

/** Widths keyed by the `<th>`'s `data-col-key`. Columns that measure to zero or
 *  a non-finite number are OMITTED, never zero-filled — a caller that got a `0`
 *  would pin the column shut. Zero is the normal answer in a test DOM with no
 *  layout engine (happy-dom), so this is the common path there, not an edge. */
export type NaturalWidths = Readonly<Record<string, number>>;

const SCROLLS = /(auto|scroll|overlay)/;

/**
 * EVERY ancestor that can scroll, plus the document scroller.
 *
 * Not just the nearest. The pass collapses the table's height, and the
 * contacts wrapper is `max-h-[70vh]` — so when the collapsed table drops under
 * that cap the WRAPPER shrinks too and the scroller above it is clamped in
 * turn (measured: `main` goes 341 -> 313). Saving the whole chain costs a walk
 * that was already happening.
 *
 * Honest about the difference between the two levels, because an earlier
 * revision was not: the INNER loss is a real, persistent 475px on the live
 * app. The outer one was clamped but restored by Chromium unaided in every
 * configuration anyone could construct, so collecting it is INSURANCE rather
 * than a fix for an observed bug.
 *
 * Reads the `overflow` SHORTHAND as well as the two axis properties, and that
 * is load-bearing rather than belt-and-braces: happy-dom does not expand the
 * shorthand, so an element styled `overflow: auto` — which is exactly what
 * Tailwind's `overflow-auto` emits, and what the contacts table uses — reports
 * `overflowY: ""` there while a browser reports `"auto"`. Checking only the
 * axes makes this function correct in production and untestable in the suite,
 * which is the wrong way round.
 */
function findScrollers(el: HTMLElement): Element[] {
  const out: Element[] = [];
  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (
      SCROLLS.test(style.overflowY) ||
      SCROLLS.test(style.overflowX) ||
      SCROLLS.test(style.overflow)
    ) {
      out.push(node);
    }
  }
  const doc = el.ownerDocument?.scrollingElement;
  if (doc && !out.includes(doc)) out.push(doc);
  return out;
}

export function measureColumnWidths(table: HTMLTableElement): NaturalWidths {
  const ths = Array.from(
    table.querySelectorAll<HTMLTableCellElement>("thead th[data-col-key]")
  );
  if (ths.length === 0) return {};

  const savedTable = {
    width: table.style.width,
    minWidth: table.style.minWidth,
    maxWidth: table.style.maxWidth,
  };
  const savedThs = ths.map((th) => ({
    th,
    width: th.style.width,
    minWidth: th.style.minWidth,
    maxWidth: th.style.maxWidth,
  }));
  // EVERY scroll container above the table, not `table.parentElement` and not
  // just the nearest.
  //
  // Dropping `parentElement` is the load-bearing half: it was right only by
  // luck — it happens to be the `overflow-auto` div today — and the symptom of
  // someone inserting a plain wrapper is exactly the scroll jump this exists to
  // prevent. That inner loss is measured and real: 475px on the live app,
  // scrolled to the bottom.
  //
  // Collecting the OUTER ones is insurance, and the docstring above says why
  // rather than overselling it. Read defensively throughout: this module is
  // handed a `<table>`, not a promise about what wraps it.
  const savedScroll = findScrollers(table).map((node) => ({
    node,
    top: node.scrollTop,
    left: node.scrollLeft,
  }));

  try {
    table.style.width = "max-content";
    table.style.minWidth = "0";
    table.style.maxWidth = "none";
    for (const th of ths) {
      th.style.width = "";
      th.style.minWidth = "";
      th.style.maxWidth = "";
    }
    const out: Record<string, number> = {};
    for (const th of ths) {
      const key = th.dataset.colKey;
      if (!key) continue;
      const px = Math.ceil(th.getBoundingClientRect().width);
      if (Number.isFinite(px) && px > 0) out[key] = px;
    }
    return out;
  } finally {
    table.style.width = savedTable.width;
    table.style.minWidth = savedTable.minWidth;
    table.style.maxWidth = savedTable.maxWidth;
    for (const saved of savedThs) {
      saved.th.style.width = saved.width;
      saved.th.style.minWidth = saved.minWidth;
      saved.th.style.maxWidth = saved.maxWidth;
    }
    // AFTER the widths, not before: the offset is only restorable once the
    // extent it is clamped against is back.
    for (const saved of savedScroll) {
      saved.node.scrollTop = saved.top;
      saved.node.scrollLeft = saved.left;
    }
  }
}

/**
 * The width to pin a default-pinned column at: its measured content width, kept
 * inside `[min, cap]`.
 *
 * `cap` is the column's old hardcoded default — `DEFAULT_ANNEX_WIDTH` (352).
 * That number was never wrong as a CEILING; it was wrong as a
 * floor-and-ceiling-in-one, which is what handing it straight to `widthStyle`
 * made it. A criterion whose rationales are all "Yes" / "No" got the same 352px
 * as one holding two sentences.
 *
 * With no measurement (nothing rendered yet, or no layout engine) the answer is
 * `cap` — i.e. exactly today's behaviour. The feature degrades to the old
 * constant rather than to a collapsed column.
 *
 * ROUNDS UP, matching `measureColumnWidths`. The two are exported side by side
 * and used to state opposite policies — `Math.ceil` there with "a fractional
 * max-content that rounds down clips the last character", `Math.round` here.
 * The contradiction was dead code today (the only caller feeds this function
 * values `measureColumnWidths` has already ceil'd) and would have come alive
 * the moment anything else called it. One rule now: a fraction of a pixel of
 * content gets a whole pixel of column.
 */
export function fitWidth(
  measured: number | undefined,
  cap: number,
  min: number
): number {
  if (measured == null || !Number.isFinite(measured) || measured <= 0) return cap;
  return Math.min(cap, Math.max(min, Math.ceil(measured)));
}

/**
 * The font loads in flight right now, as a promise that settles when they are
 * done, or `null` when there is nothing to wait for.
 *
 * `null` covers three cases on purpose. No `document.fonts` at all: happy-dom
 * and jsdom do not implement the CSS Font Loading API, and a guard that threw
 * there would take every `ContactsTable` test down with it. A set that is
 * `loaded`: the measurement just taken used the final metrics. And a set whose
 * `ready` is missing, which no browser produces but a partial polyfill could.
 *
 * `status`, not `ready` alone: `ready` always exists and resolves on the next
 * microtask when nothing is loading, so a caller that re-measured on every
 * `ready` would re-measure after every pass, which schedules another pass.
 * See "Web-font load" in the file header for why this is asked AFTER the pass.
 */
export function pendingFontLoad(doc: Document | null | undefined): Promise<unknown> | null {
  const fonts = (doc as { fonts?: Pick<FontFaceSet, "status" | "ready"> } | null | undefined)
    ?.fonts;
  if (!fonts || fonts.status !== "loading" || !fonts.ready) return null;
  return fonts.ready;
}

/**
 * How many re-measures one mounted table may owe to font loads.
 *
 * The chain described under "Web-font load" ends because `status` and `ready`
 * move together: by the time `ready` settles, `status` reads `loaded`, and the
 * next pass schedules nothing. The spec says so and Blink behaves so, but they
 * are two pieces of state, and nothing in this file can hold an engine to it.
 * One that reported `loading` next to an already-settled `ready` would turn
 * the chain into pass, microtask, bump, pass: a two-layout measurement per
 * turn, for as long as the load lasts, or for good on a request that hangs
 * (`/pr-review` PR #943 iter 2, Agent A §2.2). This makes termination a
 * property of the caller instead of the engine.
 *
 * FOUR, not one: a waiter covers every load in flight when it was armed, and
 * faces do arrive in separate loads. The regular weight first; the heavier
 * weights of headers and badges when their text is first laid out; a subset
 * fetched on demand (`latin-ext`, for a name with a diacritic the latin file
 * does not hold). That is three real loads, and one to spare. Past the cap the
 * measurement on screen stays latched, which is the behaviour before the font
 * fix, and double-click still re-fits: that bump is a user's, not a font's.
 */
export const MAX_FONT_REMEASURES = 4;
